// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Close, OverflowMenuVertical } from '@carbon/icons-react';
import {
  Button,
  Checkbox,
  DataTable,
  DataTableSkeleton,
  Pagination,
  Popover,
  PopoverContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Tag,
} from '@carbon/react';
import { useProcedureLibrary } from '../../shared/procedures';
import { useFlagOn } from '../../shared/featureFlags';
import { joinList, splitList, useUrlState } from '../../shared/url';
import type { FacetOption } from '../../shared/procedures';
import type { ArtifactSummary } from '../../shared/types/procedures';
import styles from './LibraryPage.module.css';

const PAGE_SIZES = [10, 20, 50];
const SEARCH_DEBOUNCE_MS = 300;

interface Option {
  id: string;
  text: string;
}

// Real `artifact_type` enum from `docs/upstream-specs/sop-factory.yaml`'s
// `ArtifactType` schema — 'mop'/'eop' aren't values it accepts, so selecting
// either sent an artifact_type the BFF/upstream would reject.
const ARTIFACT_TYPES: Option[] = [
  { id: 'sop', text: 'SOP' },
  { id: 'policy', text: 'Policy' },
  { id: 'fdd-rule', text: 'FDD rule' },
  { id: 'ontology-mapping', text: 'Ontology mapping' },
];

const STATUSES: Option[] = [
  { id: 'draft', text: 'Draft' },
  { id: 'in_review', text: 'In review' },
  { id: 'approved', text: 'Approved' },
  { id: 'deprecated', text: 'Deprecated' },
];

type ChipVariant = 'neutral' | 'ok' | 'info' | 'idle';

// Carbon's Tag is a rounded, translucent pill — the agreed mock wants a
// solid-background, square-cornered chip instead, so this is a plain custom
// class rather than <Tag>. Domain/Risk always use the neutral variant;
// Status picks a variant per value.
const CHIP_VARIANT_CLASS: Record<ChipVariant, string> = {
  neutral: 'chipNeutral',
  ok: 'chipOk',
  info: 'chipInfo',
  idle: 'chipIdle',
};

const STATUS_CHIP_VARIANT: Record<string, ChipVariant> = {
  approved: 'ok',
  in_review: 'info',
  draft: 'idle',
  deprecated: 'idle',
};

function Chip({ variant, children }: { variant: ChipVariant; children: React.ReactNode }) {
  return (
    <span className={`${styles.chip} ${styles[CHIP_VARIANT_CLASS[variant]]}`}>{children}</span>
  );
}

const ALL_HEADERS = [
  { key: 'title', header: 'Title', isSortable: true },
  { key: 'procedureNumber', header: 'Procedure #' },
  { key: 'domain', header: 'Domain' },
  { key: 'equipment', header: 'Equipment' },
  { key: 'risk', header: 'Risk' },
  { key: 'status', header: 'Status' },
  { key: 'version', header: 'Ver.' },
  { key: 'updated', header: 'Updated', isSortable: true },
  { key: 'owner', header: 'Owner' },
];

// No backing field in ArtifactSummary/Artifact for either — flagged with the
// same "Mocked" badge convention as StatTile until SOP Factory adds them.
const MOCKED_HEADER_KEYS = new Set(['updated', 'owner']);

// The placeholder columns are only shown behind
// procedure-library-owner-updated-enabled (off by default), so deployed builds
// never present made-up owners and dates as real.
const REAL_HEADERS = ALL_HEADERS.filter(h => !MOCKED_HEADER_KEYS.has(h.key));

interface LibraryRow {
  id: string;
  title: string;
  procedureNumber: string;
  domain: string;
  equipment: string;
  risk: string;
  status: string;
  version: string;
  updated: string;
  owner: string;
}

interface DataTableCell {
  id: string;
  value: string;
  info: { header: string };
}

function renderCell(cell: DataTableCell, onOpenTitle: () => void): React.ReactNode {
  switch (cell.info.header) {
    case 'domain':
    case 'risk':
      return cell.value ? <Chip variant="neutral">{cell.value}</Chip> : '—';
    case 'status': {
      if (!cell.value) return '—';
      return (
        <Chip variant={STATUS_CHIP_VARIANT[cell.value] ?? 'idle'}>
          {STATUSES.find(s => s.id === cell.value)?.text ?? cell.value}
        </Chip>
      );
    }
    case 'title':
      return cell.value ? (
        <button type="button" className={styles.titleLink} onClick={onOpenTitle}>
          {cell.value}
        </button>
      ) : (
        '—'
      );
    default:
      return cell.value || '—';
  }
}

// Neither field exists on ArtifactSummary/Artifact — when the flag shows them,
// the "Mocked" header badge is the disclosure; these are cycled placeholders,
// not per-item data.
const MOCK_OWNERS = ['J. Alvarez', 'M. Diallo', 'R. Chen', 'K. Osei'];
const MOCK_UPDATED_DATES = [
  '2026-08-05',
  '2026-08-04',
  '2026-08-03',
  '2026-07-31',
  '2026-07-27',
  '2026-07-24',
];

/** The library also lists draft / in-review / deprecated items, which the
 *  latest-approved detail endpoint 404s on, so the link carries the row's
 *  version (ADO #33607). Reads the artifact id from `procedureNumber`
 *  (always the bare `artifact_id`, unlike `id` below) rather than `row.id`,
 *  which is composite so two statuses of the same artifact don't collide. */
function viewPath(row: { cells: DataTableCell[] }): string {
  const artifactId = row.cells.find(c => c.info.header === 'procedureNumber')?.value;
  const version = row.cells.find(c => c.info.header === 'version')?.value;
  const query = version ? `?version=${encodeURIComponent(version)}` : '';
  return `/admin-center/procedures/view/${artifactId}${query}`;
}

function toRow(item: ArtifactSummary, index: number): LibraryRow {
  // A single artifact_id can appear once per status it fans out over (e.g. an
  // approved version and a draft of its next revision both listed at once),
  // so `id` folds in the version/index to stay unique — Carbon's DataTable
  // indexes its internal row/cell maps by this id and silently collides two
  // rows that share one. `procedureNumber` below stays the bare artifact_id
  // for anything (like `viewPath`) that needs the real id back.
  const key = `${item.artifact_id ?? `row-${index}`}:${item.version ?? index}`;
  return {
    id: key,
    title: item.title ?? item.artifact_id ?? '',
    procedureNumber: item.artifact_id ?? '',
    domain: item.categories?.domain ?? '',
    equipment: item.categories?.equipment_type ?? '',
    risk: item.categories?.risk_tier ?? '',
    status: item.status ?? '',
    version: item.version ?? '',
    updated: MOCK_UPDATED_DATES[index % MOCK_UPDATED_DATES.length],
    owner: MOCK_OWNERS[index % MOCK_OWNERS.length],
  };
}

/**
 * Single-select filter trigger: a `Button` + `Popover` radio-list, matching
 * "More filters" exactly (same component, same height/border/menu chrome)
 * instead of Carbon `Dropdown` — which computes height/border from a
 * different token formula and could never be made to visually match without
 * fighting two components' internals at once.
 */
function RadioFilterMenu({
  open,
  onOpenChange,
  align,
  allLabel,
  options,
  selected,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  align: 'bottom-start' | 'bottom-end';
  allLabel: string;
  options: Option[];
  selected: string | null;
  onSelect: (_id: string | null) => void;
}) {
  const selectedLabel = selected
    ? (options.find(o => o.id === selected)?.text ?? allLabel)
    : allLabel;
  return (
    <Popover
      open={open}
      onRequestClose={() => onOpenChange(false)}
      align={align}
      caret={false}
      border
      className={align === 'bottom-end' ? 'admin-right-aligned-menu' : 'admin-left-aligned-menu'}
    >
      <Button kind="ghost" size="md" onClick={() => onOpenChange(!open)}>
        {selectedLabel}
        <ChevronDown size={16} className={styles.filterChevron} />
      </Button>
      <PopoverContent>
        <div className={styles.filterMenu} role="menu">
          <button
            type="button"
            role="menuitemradio"
            aria-checked={selected === null}
            className={styles.filterMenuItem}
            onClick={() => {
              onSelect(null);
              onOpenChange(false);
            }}
          >
            {allLabel}
          </button>
          {options.map(opt => (
            <button
              key={opt.id}
              type="button"
              role="menuitemradio"
              aria-checked={selected === opt.id}
              className={styles.filterMenuItem}
              onClick={() => {
                onSelect(opt.id);
                onOpenChange(false);
              }}
            >
              {opt.text}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FacetGroup({
  title,
  options,
  selected,
  onToggle,
}: {
  title: string;
  options: FacetOption[];
  selected: string[];
  onToggle: (_value: string) => void;
}) {
  if (!options.length) return null;
  return (
    <div className={styles.facetGroup}>
      <div className={styles.facetGroupTitle}>{title}</div>
      {options.map(opt => (
        <Checkbox
          key={opt.value}
          id={`library-facet-${title}-${opt.value}`}
          labelText={`${opt.value} (${opt.count})`}
          checked={selected.includes(opt.value)}
          onChange={() => onToggle(opt.value)}
        />
      ))}
    </div>
  );
}

function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter(v => v !== value) : [...list, value];
}

interface ActiveFilterChip {
  key: string;
  label: string;
  onRemove: () => void;
}

function ActiveFilterChips({ chips }: { chips: ActiveFilterChip[] }) {
  if (!chips.length) return null;
  return (
    <div className={styles.activeFilters}>
      {chips.map(chip => (
        <button
          key={chip.key}
          type="button"
          className={styles.activeFilterChip}
          onClick={chip.onRemove}
        >
          {chip.label}
          <Close size={14} />
        </button>
      ))}
    </div>
  );
}

/**
 * Procedures > Library — wired to `GET /api/v1/sop-factory/artifacts` via
 * `useProcedureLibrary`. `Updated`/`Owner` have no backing field on
 * `ArtifactSummary` (or the full `Artifact` envelope) yet — see
 * admin-center/README.md — so they render as `Mocked` placeholders only behind
 * `procedure-library-owner-updated-enabled` (off by default). The row menu's
 * "Open" is wired; "Duplicate as draft" only renders behind
 * `add-procedure-enabled` and is disabled either way: SOP Factory is
 * Git-authored only, there's no create/update endpoint to wire it to. No
 * "New SOP"/"New Policy" affordance either, for the same reason — the agreed
 * mock doesn't show one.
 *
 * The committed search, filters and paging live in the query string
 * (`?q=&type=&status=&domain=&equipment=&risk=&page=&size=`) so a refresh or
 * a shared link lands on the same view. Column sort stays in DataTable.
 */
export default function LibraryPage() {
  const navigate = useNavigate();
  const [url, patchUrl] = useUrlState({
    q: '',
    type: '',
    status: '',
    domain: '',
    equipment: '',
    risk: '',
    page: '1',
    size: '10',
  });
  const { q } = url;
  const artifactType = url.type || null;
  const status = url.status || null;
  const domains = useMemo(() => splitList(url.domain), [url.domain]);
  const equipmentTypes = useMemo(() => splitList(url.equipment), [url.equipment]);
  const riskTiers = useMemo(() => splitList(url.risk), [url.risk]);
  const page = Number(url.page) || 1;
  const pageSize = Number(url.size) || 10;
  // The text box stays local so typing is responsive; the debounce commits it.
  const [searchInput, setSearchInput] = useState(q);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [openRowMenuId, setOpenRowMenuId] = useState<string | null>(null);

  // Every filter change resets to page 1 in the same URL update.
  const setFilter = (key: 'type' | 'status' | 'domain' | 'equipment' | 'risk', value: string) =>
    patchUrl({ [key]: value, page: null });
  const setDomains = (next: string[]) => setFilter('domain', joinList(next));
  const setEquipmentTypes = (next: string[]) => setFilter('equipment', joinList(next));
  const setRiskTiers = (next: string[]) => setFilter('risk', joinList(next));

  useEffect(() => {
    // Only a real change commits: on mount the box already equals the URL's
    // q, and resetting page there would lose a refreshed `?page=`.
    if (searchInput === q) return undefined;
    const t = setTimeout(() => patchUrl({ q: searchInput, page: null }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput, q, patchUrl]);

  const { items, domainFacets, equipmentFacets, riskFacets, isLoading, error, notice, retry } =
    useProcedureLibrary({ q, artifactType, status, domains, equipmentTypes, riskTiers });

  const showOwnerUpdated = useFlagOn('procedureLibraryOwnerUpdatedEnabled');
  const showDuplicate = useFlagOn('addProcedureEnabled');
  const columns = showOwnerUpdated ? ALL_HEADERS : REAL_HEADERS;

  const rows = useMemo(() => items.map(toRow), [items]);
  const activeFacetCount = domains.length + equipmentTypes.length + riskTiers.length;
  const hasAnyFilter =
    Boolean(q) || Boolean(artifactType) || Boolean(status) || activeFacetCount > 0;
  const hasAnyFacets =
    domainFacets.length > 0 || equipmentFacets.length > 0 || riskFacets.length > 0;

  const activeFilterChips: ActiveFilterChip[] = [
    ...domains.map(v => ({
      key: `domain-${v}`,
      label: `Domain: ${v}`,
      onRemove: () => setDomains(domains.filter(x => x !== v)),
    })),
    ...equipmentTypes.map(v => ({
      key: `equipment-${v}`,
      label: `Equipment: ${v}`,
      onRemove: () => setEquipmentTypes(equipmentTypes.filter(x => x !== v)),
    })),
    ...riskTiers.map(v => ({
      key: `risk-${v}`,
      label: `Risk tier: ${v}`,
      onRemove: () => setRiskTiers(riskTiers.filter(x => x !== v)),
    })),
  ];

  const clearAllFilters = () => {
    setSearchInput('');
    patchUrl({
      q: null,
      type: null,
      status: null,
      domain: null,
      equipment: null,
      risk: null,
      page: null,
    });
  };

  return (
    <div>
      <div className={styles.title}>Library</div>

      <TableToolbar>
        <TableToolbarContent>
          <TableToolbarSearch
            persistent
            size="md"
            labelText="Search artifacts"
            placeholder="Search artifacts"
            defaultValue={searchInput}
            onChange={(_e, value) => setSearchInput(value ?? '')}
          />
          <RadioFilterMenu
            open={typeMenuOpen}
            onOpenChange={setTypeMenuOpen}
            align="bottom-start"
            allLabel="Any type"
            options={ARTIFACT_TYPES}
            selected={artifactType}
            onSelect={v => setFilter('type', v ?? '')}
          />
          <RadioFilterMenu
            open={statusMenuOpen}
            onOpenChange={setStatusMenuOpen}
            align="bottom-end"
            allLabel="All statuses"
            options={STATUSES}
            selected={status}
            onSelect={v => setFilter('status', v ?? '')}
          />
          <Popover
            open={filtersOpen}
            onRequestClose={() => setFiltersOpen(false)}
            align="bottom-end"
            caret={false}
            border
            className="admin-right-aligned-menu"
          >
            <Button kind="ghost" size="md" onClick={() => setFiltersOpen(o => !o)}>
              More filters{activeFacetCount > 0 ? ` (${activeFacetCount})` : ''}
              <ChevronDown size={16} className={styles.filterChevron} />
            </Button>
            <PopoverContent>
              <div className={styles.filterPopover}>
                {hasAnyFacets ? (
                  <>
                    <FacetGroup
                      title="Domain"
                      options={domainFacets}
                      selected={domains}
                      onToggle={v => setDomains(toggle(domains, v))}
                    />
                    <FacetGroup
                      title="Equipment"
                      options={equipmentFacets}
                      selected={equipmentTypes}
                      onToggle={v => setEquipmentTypes(toggle(equipmentTypes, v))}
                    />
                    <FacetGroup
                      title="Risk tier"
                      options={riskFacets}
                      selected={riskTiers}
                      onToggle={v => setRiskTiers(toggle(riskTiers, v))}
                    />
                  </>
                ) : (
                  <div className={styles.filterPopoverEmpty}>
                    No filters available for this selection.
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
          {hasAnyFilter && (
            <Button kind="ghost" size="md" onClick={clearAllFilters}>
              Clear filters
            </Button>
          )}
        </TableToolbarContent>
      </TableToolbar>

      <ActiveFilterChips chips={activeFilterChips} />

      {error ? (
        <div className={styles.resultCount}>{error}</div>
      ) : (
        <div className={styles.resultCount}>
          {isLoading ? 'Loading…' : `${items.length} results`}
        </div>
      )}
      {notice && (
        <div className={styles.resultCount} role="status">
          {notice}
          {retry && (
            <Button kind="ghost" size="sm" onClick={retry}>
              Retry
            </Button>
          )}
        </div>
      )}

      {isLoading ? (
        <DataTableSkeleton
          headers={columns}
          rowCount={pageSize}
          showHeader={false}
          showToolbar={false}
          size="md"
        />
      ) : !error && items.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateTitle}>
            {hasAnyFilter ? 'No artifacts match these filters' : 'No artifacts yet'}
          </div>
          {hasAnyFilter && (
            <>
              <div className={styles.emptyStateSubtext}>
                Try removing a filter or clearing your search.
              </div>
              <Button kind="tertiary" size="md" onClick={clearAllFilters}>
                Clear filters
              </Button>
            </>
          )}
        </div>
      ) : (
        <>
          <DataTable rows={rows} headers={columns}>
            {({ rows: sortedRows, headers, getHeaderProps, getRowProps, getTableProps }: any) => {
              const pageRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);
              return (
                <TableContainer>
                  <Table {...getTableProps()} size="md">
                    <TableHead>
                      <TableRow>
                        {headers.map((header: any) => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                            {MOCKED_HEADER_KEYS.has(header.key) && (
                              <Tag type="gray" size="sm" className={styles.mockedTag}>
                                Mocked
                              </Tag>
                            )}
                          </TableHeader>
                        ))}
                        <TableHeader aria-hidden="true" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map((row: any) => (
                        <TableRow {...getRowProps({ row })} key={row.id}>
                          {row.cells.map((cell: DataTableCell) => (
                            <TableCell key={cell.id}>
                              {renderCell(cell, () => navigate(viewPath(row)))}
                            </TableCell>
                          ))}
                          <TableCell className={styles.rowMenuCell}>
                            <Popover
                              open={openRowMenuId === row.id}
                              onRequestClose={() => setOpenRowMenuId(null)}
                              align="bottom-end"
                              caret={false}
                              border
                              className="admin-right-aligned-menu"
                            >
                              <Button
                                kind="ghost"
                                size="sm"
                                hasIconOnly
                                iconDescription="Row actions"
                                tooltipHighContrast={false}
                                tooltipAlignment="end"
                                renderIcon={OverflowMenuVertical}
                                onClick={() =>
                                  setOpenRowMenuId(openRowMenuId === row.id ? null : row.id)
                                }
                              />
                              <PopoverContent>
                                <div className={styles.filterMenu} role="menu">
                                  <button
                                    type="button"
                                    role="menuitem"
                                    className={styles.filterMenuItem}
                                    onClick={() => {
                                      setOpenRowMenuId(null);
                                      navigate(viewPath(row));
                                    }}
                                  >
                                    Open
                                  </button>
                                  {showDuplicate && (
                                    <button
                                      type="button"
                                      role="menuitem"
                                      className={styles.filterMenuItem}
                                      disabled
                                      title="SOP Factory is read-only — authoring happens via Git PR"
                                    >
                                      Duplicate as draft
                                    </button>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            }}
          </DataTable>

          <Pagination
            page={page}
            pageSize={pageSize}
            pageSizes={PAGE_SIZES}
            totalItems={items.length}
            size="md"
            // "Next page" is the rightmost control, flush against the table's
            // own border — a centered-above tooltip clips past that edge and
            // inflates the ancestor's scrollable width into a horizontal
            // scrollbar. Growing it leftward instead avoids the edge.
            forwardTextTooltipPosition="left"
            onChange={({ page: nextPage, pageSize: nextSize }) => {
              patchUrl({ page: String(nextPage), size: String(nextSize) });
            }}
          />
        </>
      )}
    </div>
  );
}
